import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PROGRAMS } from "@/data/programs";
import { Star, Loader2, Check, MessageSquareHeart } from "lucide-react";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-accent/40 focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export default function Survey() {
  const [form, setForm] = useState({ name: "", email: "", program: "", rating: 0, enjoyed: "", improve: "", recommend: "" });
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error("Please give us a star rating.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/surveys", form);
      setDone(true);
      toast.success("Thank you for your feedback!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="survey-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Your Feedback</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            Tell us how we're doing.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Your voice helps us make our programs better for every child. It only takes a minute.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-5 sm:px-8 py-16">
        {done ? (
          <div data-testid="survey-success" className="rounded-3xl border border-slate-100 bg-white p-10 text-center shadow-lg">
            <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600"><Check className="h-8 w-8" /></span>
            <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">Thank you!</h3>
            <p className="mt-2 text-slate-600">We've received your feedback and truly appreciate you taking the time.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg space-y-5" data-testid="survey-form">
            <div className="flex items-center gap-2 text-accent">
              <MessageSquareHeart className="h-5 w-5" />
              <h2 className="font-display text-xl font-bold text-brandnavy">Program Feedback Survey</h2>
            </div>

            <div>
              <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Overall, how would you rate your experience? *</span>
              <div className="flex gap-1" data-testid="survey-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`survey-star-${n}`}
                    onClick={() => setForm({ ...form, rating: n })}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} stars`}
                  >
                    <Star className={`h-9 w-9 ${(hover || form.rating) >= n ? "fill-accent text-accent" : "text-slate-300"}`} />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name (optional)"><input data-testid="survey-name" value={form.name} onChange={update("name")} className={inputCls} /></Field>
              <Field label="Email (optional)"><input data-testid="survey-email" type="email" value={form.email} onChange={update("email")} className={inputCls} /></Field>
            </div>

            <Field label="Which program? (optional)">
              <select data-testid="survey-program" value={form.program} onChange={update("program")} className={inputCls}>
                <option value="">Select a program</option>
                {PROGRAMS.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                <option value="Summer Camp">Summer Camp</option>
                <option value="General / Multiple">General / Multiple</option>
              </select>
            </Field>

            <Field label="What did your child enjoy most?">
              <textarea data-testid="survey-enjoyed" rows={3} value={form.enjoyed} onChange={update("enjoyed")} className={inputCls} />
            </Field>

            <Field label="How can we improve?">
              <textarea data-testid="survey-improve" rows={3} value={form.improve} onChange={update("improve")} className={inputCls} />
            </Field>

            <Field label="Would you recommend us to other families?">
              <select data-testid="survey-recommend" value={form.recommend} onChange={update("recommend")} className={inputCls}>
                <option value="">Select</option>
                <option value="Yes">Yes, definitely</option>
                <option value="Maybe">Maybe</option>
                <option value="No">No</option>
              </select>
            </Field>

            <button data-testid="survey-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-6 py-4 font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-orange-400 disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <MessageSquareHeart className="h-5 w-5" />} Submit Feedback
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
